/**
 * Turn the bytes of a chosen CSV into text, whatever Excel saved them as.
 *
 * WHY THIS EXISTS (issue #87). The import read files with `File.text()`, which
 * is UTF-8 and nothing else: a byte that is not UTF-8 becomes U+FFFD, the black
 * diamond with a question mark, and the import then wrote that diamond into the
 * model as if somebody had typed it. Excel's plain "CSV (Comma delimited)" on
 * Windows saves Windows-1252, where a bullet is the single byte 0x95 and a
 * middle dot is 0xB7 - neither is UTF-8 on its own, so every dot in an edited
 * requirement came back as a diamond.
 *
 * A FILE CAN BE BOTH. The export is UTF-8 without a byte-order mark, which
 * Excel opens as Windows-1252 and saves back the same way. The characters that
 * were already there go out as the UTF-8 bytes they came in as; the ones typed
 * in Excel go out as Windows-1252. Decoding the whole file either way corrupts
 * one half, so this reads each valid UTF-8 sequence as UTF-8 and each byte that
 * cannot start or finish one as Windows-1252. The reverse mistake - a genuine
 * Windows-1252 file whose bytes happen to form UTF-8, like "Â·" - needs an
 * accented capital directly before a symbol, which requirement text does not
 * have.
 *
 * A UTF-8 file, the case that matters most, takes the first branch and never
 * reaches the byte-by-byte reading. The leading byte-order mark "CSV UTF-8"
 * writes is dropped either way, as `File.text()` dropped it.
 *
 * Pure on purpose: the bytes are the whole input, so a unit test can pin them.
 */

/** Strict, so a file that is not UTF-8 says so instead of being repaired. */
const UTF8 = new TextDecoder('utf-8', { fatal: true })

/** Windows-1252 for each byte 0x80-0xFF, built once from the platform's table. */
const CP1252_HIGH: string[] = Array.from({ length: 128 }, (_, low) =>
  new TextDecoder('windows-1252').decode(new Uint8Array([0x80 + low])),
)

/** What reading the file found. */
export interface CsvText {
  text: string
  /**
   * How many bytes were read as Windows-1252 because they were not UTF-8.
   * Zero for a UTF-8 file. Not zero is worth telling the person, since it is a
   * guess about their file, however good a one.
   */
  legacyBytes: number
}

/** True when `byte` is a UTF-8 continuation byte between `low` and `high`. */
function within(byte: number | undefined, low = 0x80, high = 0xbf): boolean {
  return byte !== undefined && byte >= low && byte <= high
}

/**
 * The length of the well-formed UTF-8 sequence starting at `at`, or 0 if none.
 *
 * The ranges are the Unicode standard's table of well-formed sequences, which
 * is what rules out overlong forms and surrogates - the same bytes a strict
 * TextDecoder refuses.
 */
function sequenceLength(bytes: Uint8Array, at: number): number {
  const lead = bytes[at]
  const [b1, b2, b3] = [bytes[at + 1], bytes[at + 2], bytes[at + 3]]
  if (lead < 0x80) return 1
  if (lead >= 0xc2 && lead <= 0xdf) return within(b1) ? 2 : 0
  if (lead >= 0xe0 && lead <= 0xef) {
    const second =
      lead === 0xe0 ? within(b1, 0xa0) : lead === 0xed ? within(b1, 0x80, 0x9f) : within(b1)
    return second && within(b2) ? 3 : 0
  }
  if (lead >= 0xf0 && lead <= 0xf4) {
    const second =
      lead === 0xf0 ? within(b1, 0x90) : lead === 0xf4 ? within(b1, 0x80, 0x8f) : within(b1)
    return second && within(b2) && within(b3) ? 4 : 0
  }
  return 0
}

/** Read a CSV's bytes as UTF-8, falling back byte by byte to Windows-1252. */
export function decodeCsv(buffer: ArrayBuffer | Uint8Array): CsvText {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer)
  // The decoder drops a leading BOM by itself; the fallback below must too.
  const start = bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf ? 3 : 0
  try {
    return { text: UTF8.decode(bytes), legacyBytes: 0 }
  } catch {
    // Not UTF-8 throughout - read it run by run below.
  }

  let text = ''
  let legacyBytes = 0
  let run = start
  let at = start
  while (at < bytes.length) {
    const length = sequenceLength(bytes, at)
    if (length > 0) {
      at += length
      continue
    }
    // Everything since `run` is well-formed UTF-8 by construction.
    text += UTF8.decode(bytes.subarray(run, at))
    text += CP1252_HIGH[bytes[at] - 0x80]
    legacyBytes += 1
    at += 1
    run = at
  }
  text += UTF8.decode(bytes.subarray(run, at))
  return { text, legacyBytes }
}
